/**
 * The client's deterministic voxel view (`class ClientWorld`).
 *
 * State model
 * -----------
 * - `chunks` is the client's voxel truth for gameplay (collision, raycast): `generateChunk(seed)` with
 *   the authoritative snapshot on top, and local pending edits re-applied (the resync rule: the server
 *   always wins, prediction is replayed on top).
 * - `overlays` is the SAME state expressed as a sparse diff against the deterministic baseline, keyed
 *   by chunk and packed chunk-local voxel index. It exists because the mesher worker is stateless: a
 *   `MesherJob` carries `(seed, edits)` and rebuilds the chunk itself, so any voxel that differs from
 *   the generated baseline (a local edit OR another player's edit seen in a snapshot) must be in the
 *   overlay or the worker will mesh bare terrain. The overlay is maintained INCREMENTALLY: a received
 *   snapshot is compared voxel by voxel against the chunk it replaces (32K palette lookups, no
 *   worldgen), and every voxel the server changed becomes an overlay entry, while every unchanged
 *   voxel keeps the entry it had. The rule that makes this exact is that a voxel WITHOUT an entry
 *   always holds its generated-baseline value, so "changed by the server" and "differs from the
 *   baseline" coincide. It also does not depend on the server bumping `rev`, which is only used for
 *   the `setBlock` round trip.
 * - `pending` is the bounded queue of unacknowledged local edits used for rollback.
 *
 * Streaming policy
 * ----------------
 * Only chunk COLUMNS are considered by distance (`dx*dx + dz*dz <= radius*radius` in chunk units).
 * Per column the vertical extent is derived from `surfaceHeightAt`, sampled on an 8-voxel stride plus
 * the far edge (25 samples per column), expanded by one chunk above and one below and capped at
 * `MAX_LAYERS_PER_COLUMN`. The full world height is never loaded. The column extent is cached until
 * the column is evicted. Eviction uses a `radius * 1.15` hysteresis so a player walking along a chunk
 * boundary does not thrash.
 *
 * The radius itself is clamped to the SHARED `MAX_VIEW_DISTANCE_CHUNKS` (`clampRadius` below), on entry
 * to the streaming machinery: the radius of the scan, of eviction and therefore of every `sub` key is
 * the clamped one, so the only view distance the authority is ever told is one it derived its own
 * residency bound from. A private literal here is exactly what used to break that - the client kept a
 * cap of 16 while the server derived its interest bound from 8, so a client told to stream 16 asked for
 * keys the authority refused with `error{code:"interest-limit"}` and, because a key is marked
 * `subscribed` the moment it is requested, never asked for them again: permanent holes in the rendered
 * world that look like a client bug.
 *
 * Interest release (eviction)
 * ---------------------------
 * `subscribed` mirrors the interest the authority holds for this client: every key in `wanted` is
 * sent as a `sub`, and evicting a key that is still subscribed RELEASES it with a matching `unsub`,
 * so server-side interest can shrink again instead of only growing. Release is bounded by the same
 * rules as subscription:
 * - only a key the client actually subscribed to is released (never inventing one);
 * - never a key it still holds (`chunks`) or still needs (`wanted`): releasing a chunk that is still
 *   rendered would silently stop its fan-out and desync it;
 * - never from `dispose()` (the connection is going away; `close` ends the session);
 * - batched like `sub` (max `SUB_BATCH_MAX` keys per frame, one flush per eviction pass), and sent
 *   AFTER the pass' `sub` frames, so a chunk re-entered on the same step is not released after being
 *   re-subscribed.
 * A released key leaves `subscribed`, so a later return re-sends `sub` and re-fetches the chunk.
 *
 * Refused subscription (inbound budget)
 * -------------------------------------
 * A key is marked `subscribed` before its frame is sent, and eviction is the only thing that clears
 * the mark. The authority refuses a `sub` it cannot afford WHOLE (one work unit per key, so a full
 * view is one burst) and grants nothing for it; a residency overflow (`interest-limit`) is instead
 * refused IN PART - it serves every key that still fits and skips the rest. Neither `error` frame names
 * keys. `lastSubBatch` remembers the keys of the frame sent last and `forgetRefusedSubBatch()` clears
 * those marks and asks the next `update` to re-run the view scan, so the keys are re-requested on the
 * next frame instead of being lost for the rest of the session (the runtime calls it from the `error`
 * handler for the two codes that mean "keys were refused"). The repair is approximate by construction,
 * because the frame we sent last is not necessarily the frame that was refused:
 * - a key the authority DID serve can be forgotten with the rest. That is safe and cheap - the next
 *   scan re-sends one `sub`, the authority answers with the same chunk, and `applyServerChunk` is
 *   idempotent - so over-forgetting costs one redundant request;
 * - a pass that exhausts the work budget is refused from the exhaustion point on, while the authority
 *   delivers at most one notice per `DROP_NOTICE_INTERVAL_MS` (5 s), so only one frame of that pass is
 *   undone. A key refused earlier in the same pass can keep its `subscribed` mark and go unrequested
 *   until the player leaves and re-enters the eviction disc, which clears the mark and re-`sub`s it.
 *
 * So: the frame-level repair converges wherever it can see the refusal, and re-entry is the complete
 * one. Nothing here relies on the repair being exact.
 *
 * The whole feature is gated on the server advertising `CAPABILITY_UNSUB` in `welcome.capabilities`
 * (see `ClientWorldDeps.capabilities`): `unsub` is additive inside protocol v1, so an older server
 * has no handler for it and the framework closes the connection, a dropped session rather than a
 * recoverable error frame. Without the capability the client is exactly the sub-only client it was.
 */

import {
  CAPABILITY_UNSUB,
  CHUNK_SIZE,
  MAX_VIEW_DISTANCE_CHUNKS,
  decodeChunkKey,
  encodeChunkKey,
  type BlockId,
  type ChunkKey,
  type ChunkSnapshot,
  type ClientMessage,
  type Vec3,
} from "@rogueworlds/shared";
import type { MesherPool } from "../mesh/mesher-pool";
import type { MesherJob, MesherResult } from "../mesh/types";
import type { ChunkRenderer } from "../render/chunk-renderer";
import {
  AIR,
  CHUNK_EDGE_M,
  Chunk,
  chunkOfVoxel,
  generateChunkChecked,
  isSolid,
  localOfVoxel,
  packVoxel,
  sampleBlock,
  surfaceHeightAtChecked,
  unpackVoxelX,
  unpackVoxelY,
  unpackVoxelZ,
  type VoxelEditAbs,
} from "./bridge";

/** A predicted, not-yet-acknowledged local edit. `x/y/z` are chunk-local (0..31). */
export interface ChunkEdit {
  readonly seq: number;
  readonly key: ChunkKey;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly block: BlockId;
  readonly prevBlock: BlockId;
  readonly rev: number;
}

export interface ClientWorldDeps {
  readonly seed: number;
  readonly renderer: Pick<ChunkRenderer, "upsert" | "remove" | "has">;
  readonly pool: Pick<MesherPool, "submit" | "onResult" | "onError" | "pending" | "workers">;
  /** Transport sink for `{ t: "sub" }` and - only when the capability below is advertised - `{ t: "unsub" }`. */
  readonly send: (message: ClientMessage) => void;
  /**
   * `welcome.capabilities`, passed through as received. `unsub` is additive in protocol v1: an older
   * server omits the field (and an unknown value must be ignored), so without `CAPABILITY_UNSUB` the
   * client must stay exactly the sub-only client it was: no handler exists for `unsub` there, and
   * the framework closes the connection instead of answering. `| undefined` is explicit so the
   * runtime can forward `welcome.capabilities` verbatim under `exactOptionalPropertyTypes`.
   */
  readonly capabilities?: readonly string[] | undefined;
}

/** Voxel stride used when sampling a column's surface for the vertical streaming window. */
const SURFACE_SAMPLE_STRIDE = 8;
/** Hard cap on chunk layers per column, whatever the terrain does. */
const MAX_LAYERS_PER_COLUMN = 5;
/** Mesher jobs in flight (pool queue + own tracking) before streaming pauses. */
const MAX_INFLIGHT_MESHES = 24;
/** Eviction happens only beyond `radius * EVICT_HYSTERESIS` chunk units. */
const EVICT_HYSTERESIS = 1.15;
/** `{ t: "sub" }` / `{ t: "unsub" }` carry at most this many keys per message. */
const SUB_BATCH_MAX = 128;
/** Locally predicted edits kept for rollback. */
const PENDING_EDIT_CAP = 64;
/**
 * Consecutive stale `chunk` frames dropped for ONE key before the client stops asking the authority for
 * that key again (see `applyServerChunk`). Exported so a test or a doc cites the bound instead of
 * restating it.
 *
 * Three, because the healthy case needs exactly ONE re-request: the authority answers a `sub` from its
 * current revision, so the reply is adopted and ends the episode. The slack above one absorbs the
 * legitimate overlap where several answers to overlapping repair `sub`s for the same key are in flight
 * at once - one frame's worth of the runtime's mailbox drain (`MAX_CHUNK_APPLIES_PER_FRAME` in
 * `main.ts` is 3) - while staying a fixed, tiny number rather than a per-view one.
 *
 * It is a COUNT, not a rate, so the bound holds at any frame rate and the class stays clock-free (a
 * token bucket would need `performance.now()` and fake timers in the tests for the same guarantee).
 * Worst case it is `STALE_RESYNC_MAX` one-key `sub`s per key per episode: 3 inbound work units (~120 KB
 * of snapshots) against the authority's 256 unit/s sustained budget - a bounded repair, not a loop.
 */
export const STALE_RESYNC_MAX = 3;

interface ColumnExtent {
  readonly min: number;
  readonly max: number;
}

export class ClientWorld {
  private readonly deps: ClientWorldDeps;
  private readonly chunks = new Map<ChunkKey, Chunk>();
  private readonly overlays = new Map<ChunkKey, Map<number, BlockId>>();
  private readonly revisions = new Map<ChunkKey, number>();
  /** FNV-1a of the last authoritative `blocks` payload per chunk: the re-mesh decision without keeping 40 KB strings. */
  private readonly serverBlocks = new Map<ChunkKey, number>();
  private readonly dirty = new Set<ChunkKey>();
  private readonly meshed = new Set<ChunkKey>();
  private readonly subscribed = new Set<ChunkKey>();
  private readonly wanted = new Set<ChunkKey>();
  private readonly columns = new Map<string, ColumnExtent>();
  private readonly inflightKeys = new Set<ChunkKey>();
  private readonly inflightJobs = new Map<number, ChunkKey>();
  private readonly pending: ChunkEdit[] = [];

  /**
   * Consecutive stale `chunk` frames dropped per key since that key was last re-armed, i.e. how much of
   * its `STALE_RESYNC_MAX` re-request budget it has spent. The entry EXISTS only while a key is being
   * dropped over and over: an adopted frame and an eviction both delete it, so the map is bounded by the
   * number of keys currently stale, never by the number of keys ever streamed.
   */
  private readonly staleResyncs = new Map<ChunkKey, number>();

  // Reused scratch buffers and structs: the streaming/scan path allocates nothing per frame.
  private readonly jobBatch: ChunkKey[] = [];
  private readonly subBatch: ChunkKey[] = [];
  private readonly unsubBatch: ChunkKey[] = [];

  /**
   * Keys of the MOST RECENT `{ t: "sub" }` frame handed to `deps.send`. The authority's refusal names
   * no keys and its notice is throttled, so the refused frame cannot be identified after the fact: the
   * frame we sent last is the only set of keys a refusal can be undone against.
   * `forgetRefusedSubBatch` uses them to undo the local "subscribed" marks that would otherwise
   * suppress the re-request for the rest of the session.
   */
  private lastSubBatch: readonly ChunkKey[] = [];

  /** Whether the next `update` must re-run the view scan even though the player did not move. */
  private rescanRequested = false;

  /** Whether the authority advertised `unsub` in `welcome.capabilities`; false for every older server. */
  private readonly unsubSupported: boolean;

  private nextJobId = 1;
  private nextSeq = 1;
  private columnX = Number.NaN;
  private columnZ = Number.NaN;
  private radius = 0;

  // One-entry voxel lookup cache: collision and raycast hit the same chunk almost every call.
  private lookupX = Number.NaN;
  private lookupY = Number.NaN;
  private lookupZ = Number.NaN;
  private lookupChunk: Chunk | undefined = undefined;

  constructor(deps: ClientWorldDeps) {
    this.deps = deps;
    // Fixed for the life of the session: the capability list only ever arrives in `welcome`.
    this.unsubSupported = deps.capabilities?.includes(CAPABILITY_UNSUB) === true;
    this.deps.pool.onResult((result: MesherResult) => {
      this.onMeshResult(result);
    });
    this.deps.pool.onError((error: Error, jobId: number) => {
      this.onMeshError(error, jobId);
    });
  }

  get chunkCount(): number {
    return this.chunks.size;
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  get meshedCount(): number {
    return this.meshed.size;
  }

  get subscribedCount(): number {
    return this.subscribed.size;
  }

  get inFlightCount(): number {
    return this.inflightJobs.size;
  }

  get mesherWorkers(): number {
    return this.deps.pool.workers;
  }

  get mesherQueued(): number {
    return this.deps.pool.pending;
  }

  /**
   * Voxel query in world voxel coordinates (integers). A chunk that is not loaded falls back to the
   * DETERMINISTIC generator (`sampleBlock`, DET-1) rather than AIR: the client must never predict a
   * fall through terrain the authority treats as solid (e.g. the basalt floor below y = 0, which is
   * outside any streamed layer).
   */
  blockAt: (bx: number, by: number, bz: number) => BlockId = (bx: number, by: number, bz: number) => {
    const cx = chunkOfVoxel(bx);
    const cy = chunkOfVoxel(by);
    const cz = chunkOfVoxel(bz);
    if (cx === this.lookupX && cy === this.lookupY && cz === this.lookupZ) {
      const cached = this.lookupChunk;
      if (cached === undefined) {
        return sampleBlock(bx, by, bz, this.deps.seed);
      }
      return cached.get(localOfVoxel(bx, cx), localOfVoxel(by, cy), localOfVoxel(bz, cz));
    }
    const chunk = this.chunks.get(encodeChunkKey(cx, cy, cz));
    this.lookupX = cx;
    this.lookupY = cy;
    this.lookupZ = cz;
    this.lookupChunk = chunk;
    if (chunk === undefined) {
      return sampleBlock(bx, by, bz, this.deps.seed);
    }
    return chunk.get(localOfVoxel(bx, cx), localOfVoxel(by, cy), localOfVoxel(bz, cz));
  };

  /**
   * `SolidQuery` for `@rogueworlds/sim`: backed by `blockAt`, filtered through the voxel registry so
   * non-solid blocks (AIR, WATER, GLASS) never collide - the same rule the authority applies.
   */
  solidAt: (bx: number, by: number, bz: number) => boolean = (bx: number, by: number, bz: number) => {
    return isSolid(this.blockAt(bx, by, bz));
  };

  /** Known authoritative `rev` of a chunk, used as the `rev` precondition of `setBlock`. */
  revisionOf(key: ChunkKey): number {
    return this.revisions.get(key) ?? 0;
  }

  /**
   * Adopts an authoritative revision, monotonically: a revision never goes backwards on the
   * authority, so a lower value is a late frame and not new information.
   *
   * Without this the chunk token only ever came from a `chunk` snapshot, so the first local edit
   * after ANY player touched that chunk was guaranteed to be refused with `stale-rev`: a visible
   * revert plus a whole chunk re-subscription over a ~40 KB payload. Every `blockAck` carries the
   * post-edit revision, which is exactly the token the next `setBlock` needs.
   */
  adoptRevision(key: ChunkKey, rev: number): void {
    const known = this.revisions.get(key);
    if (known === undefined || rev > known) {
      this.revisions.set(key, rev);
    }
  }

  /**
   * Streaming scan + mesher pump. Called once per frame by the runtime; the expensive part (the
   * column scan) only runs when the player crosses a chunk boundary, when the view distance changes,
   * or when a refused `sub` asked for a retry (`forgetRefusedSubBatch`), and job submission is
   * bounded by `MAX_INFLIGHT_MESHES`.
   *
   * `viewDistanceChunks` is clamped HERE, at the only door into this class' streaming machinery
   * (`clampRadius`), so the raw value a `welcome` named is never what this client streams - and since
   * the view is expressed to the authority exactly as the keys of its `sub` frames, the served value is
   * the clamped one. Nothing in here stores the unclamped number.
   */
  update(playerPos: Vec3, viewDistanceChunks: number): void {
    const radius = clampRadius(viewDistanceChunks);
    const columnX = Math.floor(playerPos.x / CHUNK_EDGE_M);
    const columnZ = Math.floor(playerPos.z / CHUNK_EDGE_M);
    if (
      this.rescanRequested ||
      columnX !== this.columnX ||
      columnZ !== this.columnZ ||
      radius !== this.radius
    ) {
      this.columnX = columnX;
      this.columnZ = columnZ;
      this.radius = radius;
      this.rescanRequested = false;
      this.rebuildView(columnX, columnZ, radius);
      this.evictOutside(columnX, columnZ, radius);
    }
    this.pumpJobs();
  }

  /**
   * Local sparse edit overlay for the mesher jobs of a chunk and its 26 neighbours, in ABSOLUTE voxel
   * coordinates (`VoxelEditAbs`). The full 26-neighbourhood is returned, not just the 6 faces,
   * because a mesher that regenerates neighbouring chunks for face culling must also see their edits.
   */
  editsNear(cx: number, cy: number, cz: number): VoxelEditAbs[] {
    const out: VoxelEditAbs[] = [];
    for (const [key, overlay] of this.overlays) {
      if (overlay.size === 0) {
        continue;
      }
      const coords = decodeChunkKey(key);
      if (
        Math.abs(coords.cx - cx) > 1 ||
        Math.abs(coords.cy - cy) > 1 ||
        Math.abs(coords.cz - cz) > 1
      ) {
        continue;
      }
      const baseX = coords.cx * CHUNK_SIZE;
      const baseY = coords.cy * CHUNK_SIZE;
      const baseZ = coords.cz * CHUNK_SIZE;
      for (const [packed, block] of overlay) {
        out.push({
          bx: baseX + unpackVoxelX(packed),
          by: baseY + unpackVoxelY(packed),
          bz: baseZ + unpackVoxelZ(packed),
          block,
        });
      }
    }
    return out;
  }

  /**
   * Authoritative chunk resync. Deserialize -> re-apply this chunk's pending local edits -> replace
   * the local chunk, and fold the change into the mesher overlay. The server always wins and the
   * prediction is replayed on top, so this is idempotent: a snapshot whose wire bytes match the last
   * one applied changes nothing and costs neither a re-mesh nor a job.
   *
   * INVARIANT: a snapshot is adopted only for a key the view still WANTS (`wanted`). Any other key is
   * a LATE REPLY to a `sub` the client has since abandoned - the eviction disc released it and the
   * reply crossed the walk - and the frame is dropped whole: nothing is stored, `subscribed` stays
   * clear, no revision is adopted, no mesh is queued. Re-adopting it instead would take the key
   * without any way to make it live again: the `subscribed` flag an adoption sets suppresses the
   * re-`sub` a re-entry sends, and the chunk being present suppresses the regeneration that would
   * mark it dirty, so the chunk would stay a hole (missing terrain, no server fan-out, no fresh
   * snapshot) for as long as the player stays in the area. Dropping makes re-entry the only path
   * back, and re-entry is already a complete repair: `rebuildView` regenerates the chunk, marks it
   * dirty (so the mesher rebuilds it) and re-sends `sub` (so the authority re-streams its bytes).
   *
   * Every caller is safe under that rule. `main.ts` drains its mailbox for keys this client `sub`ed,
   * so a wanted key is the normal case, and the eviction race is exactly the dropped case. The repair
   * paths are safe for the same reason: `resyncChunk`/`rejectEdit` re-`sub` a chunk we still HOLD
   * (hence still want), and a repair for a chunk evicted in the meantime has no local state left to
   * repair - the next entry regenerates it from the generator, exactly as if the repair never ran.
   *
   * Consequence for a wanted key: it is always already generated (`rebuildView` creates every key it
   * adds to `wanted`, and eviction drops the chunk and the want together), so the local chunk of a
   * wanted key is replaced rather than created, and `updateOverlay` never has to rebuild a baseline
   * for a wire frame.
   *
   * INVARIANT: revision adoption is monotonic here too, exactly as `adoptRevision` states it for a
   * `blockAck`. A snapshot whose `rev` is BELOW the revision we already hold for that chunk is a stale
   * reply and is dropped whole (no chunk replaced, no revision lowered, no re-mesh), and the chunk is
   * re-requested instead - see the branch below for why that cannot loop, and for the per-key cap
   * (`STALE_RESYNC_MAX`) that bounds the repair traffic if it ever did. Equal or higher is adopted as
   * before, and a key we hold no voxels for has nothing fresher to protect.
   */
  applyServerChunk(snapshot: ChunkSnapshot): void {
    const key = snapshot.key;
    if (!this.wanted.has(key)) {
      return;
    }
    const previous = this.chunks.get(key);
    // Monotonic adoption, mirroring `adoptRevision`: the authority's revision for a chunk only ever
    // grows, so a frame that carries LESS than the one we already hold is not new information.
    //
    // The runtime applies a `blockAck` the moment it arrives (`main.ts`) but drains this snapshot
    // mailbox LATER and in bounded batches (three per frame), so a snapshot that was queued BEFORE a
    // newer `blockAck` is applied AFTER it. Adopting it then pushes the chunk's voxels, its overlay and
    // its revision token back to the pre-edit state, and the next local `setBlock` is built on that
    // stale token and refused with `stale-rev` - a visible revert plus a whole-chunk re-subscription.
    //
    // So the stale frame is dropped whole, and the client converges instead of staying diverged by
    // re-requesting the chunk: `resyncChunk` is the documented re-stream path, and the authority always
    // answers a `sub` from its CURRENT state, so the reply carries a revision >= the one we hold. It is
    // therefore adopted, which repairs every voxel the dropped frame was behind on. That is what ends the
    // episode in the healthy case: each round trip is no older than the last, so a stale reply cannot be
    // answered by another stale reply while the authority's revision only moves forward, and the
    // per-key re-request budget below is barely touched. (A revision that moves BACKWARDS - an operator
    // restoring an older backup - is outside that argument, and no client-side resync can fix it; that
    // is the case the bound below exists for.)
    //
    // Only a chunk we actually hold can be pushed backwards: with no local voxels there is nothing
    // fresher to protect, so a frame for such a key is adopted as before (`updateOverlay` rebuilds its
    // baseline instead of diffing against a lie). On the wire path this arm is unreachable by
    // construction - a wanted key is always already generated - it keeps the rule total for a caller
    // that skipped the view scan, exactly like the `previous === undefined` branch below.
    //
    // The repair is BOUNDED per key, not unbounded: `STALE_RESYNC_MAX` consecutive stale frames may each
    // cost one re-request, and past that the frame is still dropped but nothing is asked for. The healthy
    // case never reaches the bound (the reply is adopted and re-arms the budget before another stale
    // frame can arrive); what the bound covers is the pathological case the convergence argument above
    // excludes - an authority whose revision moved BACKWARDS (an operator restoring an older backup).
    // There, no answer can ever be adopted, and without the bound the client would ask forever, ~40 KB
    // and one inbound work unit per round trip, starving its own `input`/`sub` traffic. A silenced key is
    // not lost, and it is never "recovered" by adopting the stale frame - it keeps the fresher state it
    // already had (fresher state stays the best available estimate), and:
    // - an ADOPTED frame for that key (revision >= the one we hold) re-arms the budget: that is the
    //   convergence signal, and it is the only thing here that proves the key is back in step;
    // - eviction deletes the counter with the chunk and the revision, and re-entry re-`sub`s the key
    //   from the view scan, so a return to the area streams it fresh - the complete repair this class
    //   already documents for a lost key;
    // - a new session does, by construction: a reconnect builds a fresh world (`main.ts`).
    // Deliberately NOT re-armed by a `blockAck`: `adoptRevision` is monotonic, so an ack can never bring
    // the chunk revision back into step after a rollback, and re-arming on every fan-out would hand the
    // loop its budget back one edit at a time.
    const known = this.revisions.get(key);
    if (previous !== undefined && known !== undefined && snapshot.rev < known) {
      const dropped = (this.staleResyncs.get(key) ?? 0) + 1;
      this.staleResyncs.set(key, dropped);
      if (dropped <= STALE_RESYNC_MAX) {
        this.resyncChunk(key);
      }
      return;
    }
    // Adopted, so the key is in step again: re-arm its stale-repair budget.
    this.staleResyncs.delete(key);
    const previousSignature = this.serverBlocks.get(key);
    const signature = fnv1a(snapshot.blocks);
    const pendingForChunk = this.pendingEditsFor(key);

    const incoming = Chunk.deserialize(snapshot);
    const changed = this.updateOverlay(key, snapshot, previous, incoming, pendingForChunk);
    for (const edit of pendingForChunk) {
      incoming.set(edit.x, edit.y, edit.z, edit.block);
    }

    this.serverBlocks.set(key, signature);
    this.revisions.set(key, snapshot.rev);
    this.subscribed.add(key);
    if (snapshot.cx === this.lookupX && snapshot.cy === this.lookupY && snapshot.cz === this.lookupZ) {
      this.lookupChunk = incoming;
    }
    this.chunks.set(key, incoming);

    if (previousSignature !== signature || changed) {
      this.dirty.add(key);
    }
  }

  /**
   * Optimistic local edit: the overlay and the chunk are updated immediately (0 ms felt latency) and
   * a rollback record is queued. Returns the record to acknowledge a `blockAck` against, or
   * `undefined` when the voxel is not loaded or the block did not change.
   */
  applyLocalEdit(bx: number, by: number, bz: number, block: BlockId): ChunkEdit | undefined {
    const key = encodeChunkKey(chunkOfVoxel(bx), chunkOfVoxel(by), chunkOfVoxel(bz));
    const chunk = this.chunks.get(key);
    if (chunk === undefined) {
      return undefined;
    }
    const cx = chunkOfVoxel(bx);
    const cy = chunkOfVoxel(by);
    const cz = chunkOfVoxel(bz);
    const x = localOfVoxel(bx, cx);
    const y = localOfVoxel(by, cy);
    const z = localOfVoxel(bz, cz);
    const prevBlock = chunk.get(x, y, z);
    if (prevBlock === block) {
      return undefined;
    }
    if (!chunk.set(x, y, z, block)) {
      return undefined;
    }
    let overlay = this.overlays.get(key);
    if (overlay === undefined) {
      overlay = new Map<number, BlockId>();
      this.overlays.set(key, overlay);
    }
    overlay.set(packVoxel(x, y, z), block);

    const record: ChunkEdit = {
      seq: this.nextSeq,
      key,
      x,
      y,
      z,
      block,
      prevBlock,
      rev: this.revisionOf(key),
    };
    this.nextSeq += 1;
    this.pending.push(record);
    // Bounded rollback queue: the oldest prediction is dropped (its overlay entry stays, because the
    // overlay is the local voxel state, not the rollback log).
    while (this.pending.length > PENDING_EDIT_CAP) {
      this.pending.shift();
    }
    this.dirty.add(key);
    return record;
  }

  /** Pending record for a voxel, matched by position because `blockAck` carries no `seq`. */
  pendingSeqAt(key: ChunkKey, x: number, y: number, z: number): number | undefined {
    for (const record of this.pending) {
      if (record.key === key && record.x === x && record.y === y && record.z === z) {
        return record.seq;
      }
    }
    return undefined;
  }

  /**
   * The server accepted the prediction: drop the rollback record. No re-mesh is needed - the local
   * bytes already hold the predicted value.
   */
  confirmEdit(seq: number): void {
    const index = this.indexOfPending(seq);
    if (index >= 0) {
      this.pending.splice(index, 1);
    }
  }

  /**
   * The server refused the prediction: restore the pre-edit block exactly (or adopt the authoritative
   * block the ack carries when it differs), re-mesh and re-subscribe the chunk so the client cannot
   * stay diverged from the authority.
   */
  rejectEdit(seq: number, authoritativeBlock?: BlockId): void {
    const index = this.indexOfPending(seq);
    if (index < 0) {
      return;
    }
    const record = this.pending[index];
    this.pending.splice(index, 1);
    if (record === undefined) {
      return;
    }
    const chunk = this.chunks.get(record.key);
    const finalBlock =
      authoritativeBlock !== undefined && authoritativeBlock !== record.prevBlock
        ? authoritativeBlock
        : record.prevBlock;
    if (chunk !== undefined) {
      chunk.set(record.x, record.y, record.z, finalBlock);
      const overlay = this.overlays.get(record.key);
      if (overlay !== undefined) {
        overlay.set(packVoxel(record.x, record.y, record.z), finalBlock);
      }
    }
    this.dirty.add(record.key);
    this.deps.send({ t: "sub", keys: [record.key] });
  }

  /**
   * Another player's accepted edit, i.e. the foreign half of the `blockAck` fan-out (the authority
   * sends one frame to the originator AND to every other client whose interest set holds the chunk).
   * The voxel is written through the same overlay the mesher reads (`packVoxel`), the chunk is marked
   * dirty so a rebuild is queued, and the revision is adopted, so:
   *
   * - the edit is visible immediately instead of at the next `chunk` snapshot;
   * - `blockAt`/`solidAt` stop predicting collision against a voxel the authority no longer has
   *   (the rubber-banding of walking into a phantom block a neighbour just mined).
   *
   * A voxel of a chunk this client does not hold yet is skipped: it is not streamed, so the snapshot
   * that brings the chunk already carries the edit. The revision is adopted either way.
   *
   * Returns whether the locally held voxel changed.
   */
  applyRemoteEdit(key: ChunkKey, x: number, y: number, z: number, block: BlockId, rev: number): boolean {
    this.adoptRevision(key, rev);
    const chunk = this.chunks.get(key);
    if (chunk === undefined) {
      return false;
    }
    // `set` is false when the voxel already holds that block (a duplicate fan-out, or our own
    // accepted edit arriving back through the same broadcast): nothing to re-mesh.
    if (!chunk.set(x, y, z, block)) {
      return false;
    }
    let overlay = this.overlays.get(key);
    if (overlay === undefined) {
      overlay = new Map<number, BlockId>();
      this.overlays.set(key, overlay);
    }
    overlay.set(packVoxel(x, y, z), block);
    this.dirty.add(key);
    return true;
  }

  /**
   * Re-subscribes one chunk so the authority re-streams its bytes. Called wherever the local state of a
   * chunk is known to be behind the authority and only a snapshot can repair it - a refused edit
   * (`rejectEdit`), a stale snapshot dropped by `applyServerChunk`, an `ok:false` ack with no matching
   * prediction (`main.ts`), and a mailbox overflow that dropped a frame (`main.ts`) - and
   * `applyServerChunk` diffs the answer back into the overlay and re-meshes. A repeated `sub` for a
   * chunk we already hold is the documented way to force that re-stream: it keeps the authority's
   * interest for the chunk, which is exactly what a repair needs (a release would have to be followed by
   * a new `sub`, with no benefit). The authority answers from its current state (`world-state.ts`
   * `chunkSnapshot` stamps the chunk's live revision), which is what makes the repair converge.
   */
  resyncChunk(key: ChunkKey): void {
    this.deps.send({ t: "sub", keys: [key] });
  }

  /**
   * Undoes a refused `sub` frame so its keys are requested again on the next `update`.
   *
   * Every key of `rebuildView`'s batch is marked `subscribed` BEFORE its frame is sent, and the mark
   * is only ever cleared by eviction. That is correct for a granted `sub`, but the authority refuses a
   * `sub` it cannot afford with `error{code:"too-many-messages"}` (or, for a residency overflow,
   * `interest-limit`, which serves every key that fits and skips only the rest) - and neither frame
   * names keys - so a refused key would keep its `subscribed` mark forever, never be re-requested, and
   * the player would render generated terrain (no authoritative overlay, and no fan-out for it either,
   * because a refused key never enters the authority's interest set) across that part of the view for
   * the rest of the session.
   *
   * This is the APPROXIMATE repair, not the exact one: the runtime calls it from the `error` handler,
   * and what it can identify is the LAST frame sent, not the refused one. So it clears that frame's
   * marks and lets the next `update` re-run the view scan, which re-sends them (they are still in
   * `wanted`). Two consequences, both benign:
   * - a key the authority DID serve can be forgotten too - one redundant `sub`, and the answer is the
   *   same chunk, so `applyServerChunk` changes nothing;
   * - a pass that exhausts the work budget is refused from the exhaustion point on, while the authority
   *   sends at most one notice per `DROP_NOTICE_INTERVAL_MS` (5 s), so only one frame of that pass is
   *   undone and a key refused earlier in it can stay marked `subscribed`, unrequested, until the
   *   player leaves and re-enters the eviction disc - which clears the mark and re-`sub`s it.
   *
   * No chunk-boundary crossing is needed for the frame-level repair, and a key the view no longer wants
   * is simply not re-sent (the release already dropped it locally, and a refused key never reached the
   * authority's interest set, so it needs no `unsub`).
   *
   * Returns how many keys were actually forgotten, so a duplicate `error` frame for a refusal already
   * undone reports 0 instead of re-claiming the batch size (the marks are the state, not the frame).
   */
  forgetRefusedSubBatch(): number {
    let forgotten = 0;
    for (const key of this.lastSubBatch) {
      if (this.subscribed.delete(key)) {
        forgotten += 1;
      }
    }
    if (forgotten > 0) {
      this.rescanRequested = true;
    }
    return forgotten;
  }

  /** Drops every worker job, mesh and chunk reference. GPU/pool ownership stays with the runtime. */
  dispose(): void {
    for (const key of this.chunks.keys()) {
      this.deps.renderer.remove(key);
    }
    this.chunks.clear();
    this.overlays.clear();
    this.revisions.clear();
    this.serverBlocks.clear();
    this.dirty.clear();
    this.meshed.clear();
    this.subscribed.clear();
    this.wanted.clear();
    this.columns.clear();
    this.inflightKeys.clear();
    this.inflightJobs.clear();
    this.pending.length = 0;
    // Spent stale-repair budgets go with the session, like the revisions they were counted against.
    this.staleResyncs.clear();
    this.jobBatch.length = 0;
    this.subBatch.length = 0;
    // The refusal memory dies with the session: a fresh `welcome` builds a fresh world.
    this.lastSubBatch = [];
    this.rescanRequested = false;
    // Never release interest from `dispose`: the session is over, so there is nobody left to keep in
    // step with - and a frame the closing transport drops is worse than no frame.
    this.unsubBatch.length = 0;
    this.lookupChunk = undefined;
  }

  // ---------------------------------------------------------------- internals

  private indexOfPending(seq: number): number {
    for (let i = 0; i < this.pending.length; i++) {
      const record = this.pending[i];
      if (record !== undefined && record.seq === seq) {
        return i;
      }
    }
    return -1;
  }

  private pendingEditsFor(key: ChunkKey): { x: number; y: number; z: number; block: BlockId }[] {
    const out: { x: number; y: number; z: number; block: BlockId }[] = [];
    for (const record of this.pending) {
      if (record.key === key) {
        out.push({ x: record.x, y: record.y, z: record.z, block: record.block });
      }
    }
    return out;
  }

  /**
   * Folds a received snapshot into the overlay and reports whether the overlay changed.
   *
   * `previous` is the chunk being replaced and `incoming` the raw server chunk, both as palette
   * indices, so the loop is a straight per-voxel id comparison with those not in either of them. The
   * invariant that makes this exact without touching the generator is inherited: a voxel with no
   * overlay entry holds its baseline value, so a voxel the server changed from its previous value is
   * a voxel that now differs from the baseline. Entries for voxels the server did not touch are left
   * alone, whatever produced them. Pending local predictions are written last so they keep winning.
   *
   * The only case that cannot use the diff is a snapshot for a chunk the client never generated. That
   * is no longer a wire case - `applyServerChunk` adopts wanted keys only, and a wanted key is always
   * generated already - so this branch is what a caller that skipped the view scan gets, which is the
   * state-level tests. It stays, and rebuilds the baseline instead of baselining against a lie.
   */
  private updateOverlay(
    key: ChunkKey,
    snapshot: ChunkSnapshot,
    previous: Chunk | undefined,
    incoming: Chunk,
    pendingForChunk: readonly { x: number; y: number; z: number; block: BlockId }[],
  ): boolean {
    let overlay = this.overlays.get(key);
    let changed = false;
    if (previous === undefined) {
      const baseline = generateChunkChecked(this.deps.seed, snapshot.cx, snapshot.cy, snapshot.cz);
      if (overlay === undefined) {
        overlay = new Map<number, BlockId>();
        this.overlays.set(key, overlay);
      } else if (overlay.size > 0) {
        overlay.clear();
        changed = true;
      }
      for (let y = 0; y < CHUNK_SIZE; y++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          for (let x = 0; x < CHUNK_SIZE; x++) {
            const block = incoming.get(x, y, z);
            if (block !== baseline.get(x, y, z)) {
              overlay.set(packVoxel(x, y, z), block);
              changed = true;
            }
          }
        }
      }
    } else {
      const previousPalette = previous.palette;
      const previousBlocks = previous.blocks;
      const incomingPalette = incoming.palette;
      const incomingBlocks = incoming.blocks;
      for (let i = 0; i < incomingBlocks.length; i++) {
        const wasBlock = previousPalette[previousBlocks[i] ?? 0] ?? AIR;
        const isBlock = incomingPalette[incomingBlocks[i] ?? 0] ?? AIR;
        if (wasBlock === isBlock) {
          continue;
        }
        if (overlay === undefined) {
          overlay = new Map<number, BlockId>();
          this.overlays.set(key, overlay);
        }
        overlay.set(i, isBlock);
        changed = true;
      }
    }
    if (pendingForChunk.length > 0) {
      if (overlay === undefined) {
        overlay = new Map<number, BlockId>();
        this.overlays.set(key, overlay);
      }
      for (const edit of pendingForChunk) {
        const packed = packVoxel(edit.x, edit.y, edit.z);
        if (overlay.get(packed) !== edit.block) {
          overlay.set(packed, edit.block);
          changed = true;
        }
      }
    }
    if (overlay !== undefined && overlay.size === 0) {
      this.overlays.delete(key);
    }
    return changed;
  }

  private columnExtent(cx: number, cz: number): ColumnExtent {
    const columnKey = `${cx},${cz}`;
    const cached = this.columns.get(columnKey);
    if (cached !== undefined) {
      return cached;
    }
    const baseX = cx * CHUNK_SIZE;
    const baseZ = cz * CHUNK_SIZE;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (let i = 0; i <= CHUNK_SIZE; i += SURFACE_SAMPLE_STRIDE) {
      const ox = i < CHUNK_SIZE ? i : CHUNK_SIZE - 1;
      for (let j = 0; j <= CHUNK_SIZE; j += SURFACE_SAMPLE_STRIDE) {
        const oz = j < CHUNK_SIZE ? j : CHUNK_SIZE - 1;
        const height = surfaceHeightAtChecked(this.deps.seed, baseX + ox, baseZ + oz);
        if (height < minY) {
          minY = height;
        }
        if (height > maxY) {
          maxY = height;
        }
      }
    }
    if (!Number.isFinite(minY) || !Number.isFinite(maxY)) {
      minY = 0;
      maxY = 0;
    }
    const min = Math.floor(minY / CHUNK_SIZE) - 1;
    let max = Math.floor(maxY / CHUNK_SIZE) + 1;
    if (max - min + 1 > MAX_LAYERS_PER_COLUMN) {
      max = min + MAX_LAYERS_PER_COLUMN - 1;
    }
    const extent: ColumnExtent = { min, max };
    this.columns.set(columnKey, extent);
    return extent;
  }

  private rebuildView(columnX: number, columnZ: number, radius: number): void {
    this.wanted.clear();
    const radiusSq = radius * radius;
    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dz * dz > radiusSq) {
          continue;
        }
        const cx = columnX + dx;
        const cz = columnZ + dz;
        const extent = this.columnExtent(cx, cz);
        for (let cy = extent.min; cy <= extent.max; cy++) {
          const key = encodeChunkKey(cx, cy, cz);
          this.wanted.add(key);
          if (!this.chunks.has(key)) {
            this.chunks.set(key, generateChunkChecked(this.deps.seed, cx, cy, cz));
            this.dirty.add(key);
          }
        }
      }
    }

    this.subBatch.length = 0;
    for (const key of this.wanted) {
      if (this.subscribed.has(key)) {
        continue;
      }
      this.subscribed.add(key);
      this.subBatch.push(key);
      if (this.subBatch.length >= SUB_BATCH_MAX) {
        this.flushSubBatch();
      }
    }
    this.flushSubBatch();
  }

  private flushSubBatch(): void {
    if (this.subBatch.length === 0) {
      return;
    }
    const keys = this.subBatch.slice();
    // Remembered before the send: a refusal of this frame arrives later, and the local `subscribed`
    // marks are already set - only the keys of the frame we sent last can be the refused ones.
    this.lastSubBatch = keys;
    this.deps.send({ t: "sub", keys });
    this.subBatch.length = 0;
  }

  private evictOutside(columnX: number, columnZ: number, radius: number): void {
    const limit = radius * EVICT_HYSTERESIS;
    const limitSq = limit * limit;
    // Scratched per pass, like `subBatch`: the release of a long walk is a couple of frames, never one
    // frame per chunk. `rebuildView` has already sent this pass' `sub` frames, so a chunk re-entered on
    // this step is subscribed first and only then considered for release (it is in `wanted` by then).
    this.unsubBatch.length = 0;
    for (const key of this.chunks.keys()) {
      const coords = decodeChunkKey(key);
      const dx = coords.cx - columnX;
      const dz = coords.cz - columnZ;
      if (dx * dx + dz * dz > limitSq && !this.inflightKeys.has(key)) {
        // Release interest in the SAME act as dropping the chunk: the authority's interest set then
        // shrinks with the view. Only for a key we actually subscribed, only when the server
        // advertises the capability, and never for a key the rebuilt view still wants - releasing a
        // chunk we still render would silently end its fan-out and leave us diverged.
        const release = this.unsubSupported && this.subscribed.has(key) && !this.wanted.has(key);
        this.evict(key);
        if (release) {
          this.queueUnsub(key);
        }
      }
    }
    for (const columnKey of this.columns.keys()) {
      const comma = columnKey.indexOf(",");
      const cx = Number(columnKey.slice(0, comma));
      const cz = Number(columnKey.slice(comma + 1));
      const dx = cx - columnX;
      const dz = cz - columnZ;
      if (dx * dx + dz * dz > limitSq) {
        this.columns.delete(columnKey);
      }
    }
    this.flushUnsubBatch();
  }

  /** Queues one release, flushing a full batch so the keys of one pass never exceed `SUB_BATCH_MAX`. */
  private queueUnsub(key: ChunkKey): void {
    this.unsubBatch.push(key);
    if (this.unsubBatch.length >= SUB_BATCH_MAX) {
      this.flushUnsubBatch();
    }
  }

  private flushUnsubBatch(): void {
    if (this.unsubBatch.length === 0) {
      return;
    }
    // Only ever queued from `evictOutside` behind the `unsubSupported` gate above; an old server never
    // sees this frame.
    this.deps.send({ t: "unsub", keys: this.unsubBatch.slice() });
    this.unsubBatch.length = 0;
  }

  private evict(key: ChunkKey): void {
    const coords = decodeChunkKey(key);
    this.chunks.delete(key);
    this.overlays.delete(key);
    this.revisions.delete(key);
    this.serverBlocks.delete(key);
    this.dirty.delete(key);
    this.meshed.delete(key);
    // The spent stale-repair budget goes with the revision and the voxels: a re-entered key is a fresh
    // key, and it must not inherit a cap that another, long-gone stretch of this key's life exhausted.
    this.staleResyncs.delete(key);
    // Dropping our "held" flag is what makes the release safe: a later return to this chunk re-sends
    // `sub` and receives a fresh snapshot.
    this.subscribed.delete(key);
    this.wanted.delete(key);
    if (coords.cx === this.lookupX && coords.cy === this.lookupY && coords.cz === this.lookupZ) {
      this.lookupChunk = undefined;
    }
    this.deps.renderer.remove(key);
  }

  private pumpJobs(): void {
    if (this.dirty.size === 0) {
      return;
    }
    this.jobBatch.length = 0;
    for (const key of this.dirty) {
      // The cap counts what is already in flight PLUS what this batch will submit; the pool's own
      // queue is a second guard. Both are needed: without the batch term the whole dirty set (one
      // job per chunk in view) would be handed to the pool in a single frame.
      if (
        this.inflightJobs.size + this.jobBatch.length >= MAX_INFLIGHT_MESHES ||
        this.deps.pool.pending >= MAX_INFLIGHT_MESHES
      ) {
        break;
      }
      if (this.inflightKeys.has(key) || !this.chunks.has(key)) {
        continue;
      }
      this.jobBatch.push(key);
    }
    for (const key of this.jobBatch) {
      const chunk = this.chunks.get(key);
      if (chunk === undefined) {
        this.dirty.delete(key);
        continue;
      }
      const coords = decodeChunkKey(key);
      const id = this.nextJobId;
      this.nextJobId += 1;
      this.inflightJobs.set(id, key);
      this.inflightKeys.add(key);
      this.dirty.delete(key);
      const job: MesherJob = {
        id,
        cx: coords.cx,
        cy: coords.cy,
        cz: coords.cz,
        seed: this.deps.seed,
        edits: this.editsNear(coords.cx, coords.cy, coords.cz),
      };
      this.deps.pool.submit(job);
    }
    this.jobBatch.length = 0;
  }

  private onMeshResult(result: MesherResult): void {
    this.inflightJobs.delete(result.id);
    this.inflightKeys.delete(result.key);
    const loaded = this.chunks.has(result.key);
    if (!loaded || !this.wanted.has(result.key)) {
      if (this.deps.renderer.has(result.key)) {
        this.deps.renderer.remove(result.key);
      }
      return;
    }
    this.deps.renderer.upsert(result.key, result.mesh);
    this.meshed.add(result.key);
  }

  private onMeshError(error: Error, jobId: number): void {
    const key = this.inflightJobs.get(jobId);
    this.inflightJobs.delete(jobId);
    if (key !== undefined) {
      this.inflightKeys.delete(key);
    }
    // The chunk is deliberately not re-queued: a persistent mesher failure would otherwise spin.
    console.error(`[client-world] mesher failed for ${key ?? "?"}: ${error.message}`);
  }
}

/**
 * The view radius this client actually streams, for any value a `welcome` (or the bootstrap
 * `/api/config`) supplied.
 *
 * It clamps to the SHARED `MAX_VIEW_DISTANCE_CHUNKS` - the same constant the authority clamps the
 * `viewDistanceChunks` it advertises to and derives its residency bound from (`CLIENT_MAX_WORKING_SET_CHUNKS`
 * / `MAX_INTEREST_CHUNKS` in `apps/server/src/world/room.ts`) - so a hostile or newly-raised
 * `viewDistanceChunks` cannot make this client stream more chunk columns than the authority admits.
 * That is not a tuning question: a key the authority refuses is never asked for again (the local
 * `subscribed` mark is set when the `sub` is sent and only eviction clears it), so every refused key is
 * a permanent hole in the rendered world.
 *
 * Total for any input, and deliberately unchanged for the ordinary ones: a non-finite value and
 * anything below 1 stream radius 1 (the smallest view there is), a fraction floors, and anything above
 * the cap is the cap.
 */
function clampRadius(value: number): number {
  if (!Number.isFinite(value)) {
    return 1;
  }
  const rounded = Math.floor(value);
  if (rounded < 1) {
    return 1;
  }
  return rounded > MAX_VIEW_DISTANCE_CHUNKS ? MAX_VIEW_DISTANCE_CHUNKS : rounded;
}

/**
 * Whether a `welcome` must tear the live world down and build a new one.
 *
 * The room delivers `welcome` TWICE on purpose (`apps/server/src/world/room.ts`: once from `onJoin`
 * and again when our `hello` arrives), and both frames carry the same `playerId`. Rebuilding on the
 * re-delivery re-sends `sub` for the whole view at once (`ceil(viewDistanceChunks disc keys / 128)`
 * frames), which is precisely the burst the authority's inbound work budget refuses - and a refused
 * `sub` grants nothing, so the rebuilt world would render generated terrain across part of the view
 * (with collision rubber-banding) until the player walks out of the eviction disc and back.
 *
 * So: only an id change rebuilds. An empty `existingPlayerId` means "no world yet" (first join), and a
 * different one means a fresh join after a drop, which really is a new session - the transport has no
 * resume path, so the world of the dropped session must go.
 */
export function shouldRebuildWorldFor(existingPlayerId: string, incomingPlayerId: string): boolean {
  return existingPlayerId === "" || existingPlayerId !== incomingPlayerId;
}

/** FNV-1a over a chunk payload: a cheap, collision-safe-enough "did the bytes change" signature. */
function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
